/**
 * Routine pieces the pages share (slice 5 spec §11): the last run's mark
 * with its icon and colour, a run's icon, and the routine lines in a chat
 * (§11.4), which link to Routine detail.
 */
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import {
  Check,
  CircleAlert,
  Clock,
  Loader,
  Minus,
  Monitor,
  Pause,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { TONE_TEXT, type LastRunIcon, type LastRunView } from "./routine-view";

const ICONS: Record<LastRunIcon, LucideIcon> = {
  check: Check,
  pause: Pause,
  dash: Minus,
  alert: CircleAlert,
  phone: Smartphone,
  screen: Monitor,
  running: Loader,
};

export function RunIcon({ icon, className }: { icon: LastRunIcon; className?: string }) {
  const Icon = ICONS[icon];
  return <Icon aria-hidden className={cn("size-3.5 shrink-0", className)} strokeWidth={2} />;
}

/** "Texted to you · 7:01 am", in its colour, with its icon. */
export function LastRunMark({ view, className }: { view: LastRunView; className?: string }) {
  return (
    <span
      data-last-run={view.icon ?? "none"}
      className={cn("flex min-w-0 items-center gap-1.5 text-meta", TONE_TEXT[view.tone], className)}
    >
      {view.icon ? <RunIcon icon={view.icon} /> : null}
      <span className="truncate">{view.text}</span>
    </span>
  );
}

/**
 * A routine line in a chat (board 83): "Made a reminder: Call Miller · Today,
 * 6:30 pm", or a reminder's "Reminder · 6:30 pm", with a clock, linking to
 * Routine detail.
 */
export function RoutineLine({ routineId, text }: { routineId: string; text: string }) {
  return (
    <Link
      href={`/routines/${routineId}`}
      data-routine-line={routineId}
      className="inline-flex items-start gap-1.5 underline-offset-2 hover:text-foreground hover:underline"
    >
      <Clock aria-hidden className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.8} />
      <span>{text}</span>
    </Link>
  );
}
